/* eslint-disable no-await-in-loop */
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  Link,
  Typography,
} from '@mui/material';
import { CheckCircle, Clock, X } from 'lucide-react';
import { supabase } from '../../../services/supabase';

interface CleanupItem {
  type: 'folder' | 'file';
  path: string;
  displayName: string;
  entityType: 'story' | 'show';
  entityId: string;
  adminUrl?: string;
}

interface ItemState {
  selected: boolean;
  status: 'pending' | 'deleting' | 'completed' | 'error';
  error?: string;
}

interface ScanResponse {
  items: CleanupItem[];
  totalCount: number;
}

interface DeleteResult {
  path: string;
  success: boolean;
  error?: string;
}

type CleanupMode = 'orphan-entities' | 'unused-files' | 'old-variants';

interface StorageCleanupDialogProps {
  open: boolean;
  mode: CleanupMode;
  onClose: () => void;
}

type CleanupState =
  | 'scanning'
  | 'scan_complete'
  | 'deleting'
  | 'complete'
  | 'error';

export const StorageCleanupDialog: React.FC<StorageCleanupDialogProps> = ({
  open,
  mode,
  onClose,
}) => {
  const [state, setState] = useState<CleanupState>('scanning');
  const [items, setItems] = useState<CleanupItem[]>([]);
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({});
  const [error, setError] = useState<string | null>(null);
  const [currentDeleteIndex, setCurrentDeleteIndex] = useState(-1);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setState('scanning');
      setItems([]);
      setItemStates({});
      setError(null);
      setCurrentDeleteIndex(-1);
      handleScan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const handleScan = async () => {
    setState('scanning');
    setError(null);

    try {
      const { data, error: scanError } = await supabase.functions.invoke(
        'storage-cleanup/scan',
        {
          body: { mode },
        }
      );

      if (scanError) {
        throw scanError;
      }

      const scanResult: ScanResponse = data;

      // Initialize all items as selected by default
      const initialStates: Record<string, ItemState> = {};
      scanResult.items.forEach(item => {
        initialStates[item.path] = {
          selected: true,
          status: 'pending',
        };
      });

      setItems(scanResult.items);
      setItemStates(initialStates);
      setState('scan_complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scan storage');
      setState('error');
    }
  };

  const handleItemToggle = (path: string) => {
    setItemStates(prev => {
      const currentState = prev[path];
      if (!currentState) return prev;

      return {
        ...prev,
        [path]: {
          ...currentState,
          selected: !currentState.selected,
        },
      };
    });
  };

  const handleSelectAll = () => {
    const allSelected = items.every(item => itemStates[item.path]?.selected);
    const newSelected = !allSelected;

    setItemStates(prev => {
      const updated = { ...prev };
      items.forEach(item => {
        updated[item.path] = {
          ...updated[item.path],
          selected: newSelected,
          status: updated[item.path]?.status || 'pending',
        };
      });
      return updated;
    });
  };

  const handleDelete = async () => {
    const selectedPaths = items
      .filter(item => itemStates[item.path]?.selected)
      .map(item => item.path);

    if (selectedPaths.length === 0) {
      setError('Please select at least one item to delete');
      return;
    }

    setState('deleting');
    setError(null);

    try {
      // Sequential deletion with progress updates
      for (let i = 0; i < selectedPaths.length; i++) {
        const path = selectedPaths[i];
        if (!path) continue;

        setCurrentDeleteIndex(i);

        // Update status to deleting
        setItemStates(prev => ({
          ...prev,
          [path]: {
            ...prev[path]!,
            status: 'deleting',
          },
        }));

        try {
          const { data, error: deleteError } = await supabase.functions.invoke(
            'storage-cleanup/delete',
            {
              body: { items: [path] },
            }
          );

          if (deleteError) {
            throw deleteError;
          }

          const deleteResult: { results: DeleteResult[] } = data;
          const result = deleteResult.results?.[0];

          // Update status based on result
          setItemStates(prev => ({
            ...prev,
            [path]: {
              ...prev[path]!,
              status: result?.success ? 'completed' : 'error',
              error: result?.error || undefined,
            },
          }));
        } catch (itemError) {
          setItemStates(prev => ({
            ...prev,
            [path]: {
              ...prev[path]!,
              status: 'error',
              error:
                itemError instanceof Error
                  ? itemError.message
                  : 'Unknown error',
            },
          }));
        }
      }

      setState('complete');
      setCurrentDeleteIndex(-1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete items');
      setState('error');
      setCurrentDeleteIndex(-1);
    }
  };

  const renderStatusIcon = (item: CleanupItem) => {
    const itemState = itemStates[item.path];

    switch (itemState?.status) {
      case 'deleting':
        return <CircularProgress size={20} />;
      case 'completed':
        return <CheckCircle color="success" />;
      case 'error':
        return <X color="red" />;
      case 'pending':
        if (state === 'deleting') {
          return <Clock color="gray" />;
        }
        return null;
      default:
        return null;
    }
  };

  const renderItemText = (item: CleanupItem) => {
    const itemState = itemStates[item.path];
    const isFolder = item.type === 'folder';

    // Parse displayName: "filename.ext (entity: slug, ...)" or "Entity name / subfolder"
    // Extract filename for bold styling
    const parseDisplayName = (displayName: string) => {
      // Check for old-variants format: "filename (story: slug, old variant of: ...)"
      const variantMatch = displayName.match(/^([^\s(]+)\s+\((.+)\)$/);
      if (variantMatch) {
        return {
          filename: variantMatch[1],
          details: `(${variantMatch[2]})`,
        };
      }

      // Check for "/" separator format
      const parts = displayName.split(' / ');
      if (parts.length > 1) {
        return {
          entityName: parts[0],
          path: parts.slice(1).join(' / '),
        };
      }

      // Default: entire string is the filename
      return { filename: displayName };
    };

    const parsed = parseDisplayName(item.displayName);

    if (item.adminUrl) {
      // Has admin URL - make it clickable
      if ('entityName' in parsed && parsed.path) {
        // Format: "Entity name / path"
        return (
          <Typography variant="body2">
            <Link
              href={item.adminUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {parsed.entityName}
            </Link>
            {' / '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {parsed.path}
            </Box>
            {itemState?.status === 'error' && itemState.error && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  color: 'error.main',
                }}
              >
                Error: {itemState.error}
              </Box>
            )}
          </Typography>
        );
      }

      // Format: "filename (details)" - make filename bold, keep standard color
      return (
        <Typography variant="body2">
          <Box
            component="span"
            sx={{ fontWeight: 'bold', color: 'text.primary' }}
          >
            {parsed.filename}
          </Box>
          {parsed.details && (
            <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
              {parsed.details}
            </Box>
          )}
          {item.adminUrl && (
            <Link
              href={item.adminUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              sx={{ ml: 1, fontSize: '0.875rem' }}
            >
              View
            </Link>
          )}
          {itemState?.status === 'error' && itemState.error && (
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: '0.75rem',
                mt: 0.5,
                color: 'error.main',
              }}
            >
              Error: {itemState.error}
            </Box>
          )}
        </Typography>
      );
    }

    // No admin URL
    return (
      <Typography variant="body2">
        <Box
          component="span"
          sx={{ fontWeight: isFolder ? 'bold' : 'bold', color: 'text.primary' }}
        >
          {parsed.filename}
        </Box>
        {parsed.details && (
          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
            {parsed.details}
          </Box>
        )}
        {itemState?.status === 'error' && itemState.error && (
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              mt: 0.5,
              color: 'error.main',
            }}
          >
            Error: {itemState.error}
          </Box>
        )}
      </Typography>
    );
  };

  const selectedCount = items.filter(
    item => itemStates[item.path]?.selected
  ).length;
  const isDeleteDisabled = state !== 'scan_complete' || selectedCount === 0;

  const modeTitle =
    mode === 'orphan-entities'
      ? 'Clean Up Orphaned Folders'
      : mode === 'unused-files'
        ? 'Clean Up Non-Conventional Files'
        : 'Clean Up Old Format Variants';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{modeTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {state === 'scanning' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1">Scanning storage...</Typography>
          </Box>
        )}

        {(state === 'scan_complete' ||
          state === 'deleting' ||
          state === 'complete') &&
          (items.length === 0 ? (
            <Box sx={{ textAlign: 'left', py: 2 }}>
              <Typography variant="h6" color="success.main">
                No items found!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mode === 'orphan-entities'
                  ? 'All story and show folders exist in the database.'
                  : mode === 'unused-files'
                    ? 'All files follow the conventional naming from media-config.'
                    : 'No old format variants found - all files are current conventional versions.'}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Found {items.length} items:
              </Typography>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Checkbox
                  checked={items.every(item => itemStates[item.path]?.selected)}
                  indeterminate={
                    selectedCount > 0 && selectedCount < items.length
                  }
                  onChange={handleSelectAll}
                  disabled={state === 'deleting'}
                />
                <Typography variant="body2">
                  Select all ({selectedCount} of {items.length} selected)
                </Typography>
              </Box>

              <List
                sx={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  py: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {items.map(item => {
                  const statusIcon = renderStatusIcon(item);
                  return (
                    <ListItem
                      key={item.path}
                      dense
                      sx={{
                        cursor: state !== 'deleting' ? 'pointer' : 'default',
                        '&:hover':
                          state !== 'deleting'
                            ? { backgroundColor: 'action.hover' }
                            : {},
                        py: 0.25,
                        px: 1,
                      }}
                      onClick={e => {
                        if (
                          state !== 'deleting' &&
                          e.target === e.currentTarget
                        ) {
                          handleItemToggle(item.path);
                        }
                      }}
                    >
                      {state === 'deleting' ? (
                        statusIcon && (
                          <ListItemIcon sx={{ minWidth: 24, pl: 1 }}>
                            {statusIcon}
                          </ListItemIcon>
                        )
                      ) : (
                        <ListItemIcon
                          onClick={e => e.stopPropagation()}
                          sx={{ minWidth: 24, pl: 1 }}
                        >
                          <Checkbox
                            checked={itemStates[item.path]?.selected === true}
                            onChange={() => handleItemToggle(item.path)}
                            edge="start"
                            size="small"
                          />
                        </ListItemIcon>
                      )}

                      <Box sx={{ flex: 1 }}>{renderItemText(item)}</Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
      </DialogContent>
      <DialogActions>
        {state === 'complete' ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={state === 'deleting'}>
              Cancel
            </Button>
            {items.length > 0 && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={isDeleteDisabled}
                startIcon={
                  state === 'deleting' ? <CircularProgress size={20} /> : null
                }
              >
                {state === 'deleting'
                  ? `Deleting... (${currentDeleteIndex + 1}/${selectedCount})`
                  : `Delete ${selectedCount} Selected`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
